<div class="bai-insights-root">

  <!-- ======================================================================
       Header
  ====================================================================== -->
  <div class="bai-insights-header">
    <div class="bai-insights-header__title-row">
      <div class="bai-insights-header__title-group">
        <h2 class="bai-insights-header__title">
          <i class="pi pi-microchip-ai bai-insights-header__title-icon"></i>
          AI Insights
          @if (unseenCount() > 0) {
            <span class="bai-insights-badge" [attr.aria-label]="unseenCount() + ' new insights'">
              {{ unseenCount() }}
            </span>
          }
        </h2>
        <p class="bai-insights-header__subtitle">
          AI-powered client intelligence from every conversation, in seconds.
        </p>
      </div>
      <div class="bai-insights-header__actions">
        <button
          type="button"
          class="bai-insights-btn bai-insights-btn--secondary"
          (click)="insightsState.ensureState('')"
          aria-label="Refresh insights"
          pTooltip="Refresh Insights"
          tooltipPosition="bottom"
        >
          <i class="pi pi-refresh"></i>
          <span>Refresh</span>
        </button>
        <button
          type="button"
          class="bai-insights-btn bai-insights-btn--primary"
          (click)="openNoteModal()"
          aria-label="Add advisor note"
        >
          <i class="pi pi-plus"></i>
          <span>Add Note</span>
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    @if (state(); as s) {
      <div class="bai-insights-kpi-strip" role="list" aria-label="AI Insights summary">
        <div class="bai-insights-kpi" role="listitem">
          <span class="bai-insights-kpi__icon"><i class="pi pi-clock"></i></span>
          <div class="bai-insights-kpi__body">
            <span class="bai-insights-kpi__value">{{ timeSavedLabel(s.timeSavedMinutes) }}</span>
            <span class="bai-insights-kpi__label">Time Saved</span>
          </div>
        </div>
        <div class="bai-insights-kpi" role="listitem">
          <span class="bai-insights-kpi__icon bai-insights-kpi__icon--indigo"><i class="pi pi-sparkles"></i></span>
          <div class="bai-insights-kpi__body">
            <span class="bai-insights-kpi__value">{{ s.newInsights24h }}<span class="bai-insights-kpi__sub"> / {{ s.newInsights7d }}</span></span>
            <span class="bai-insights-kpi__label">New Insights (24h / 7d)</span>
          </div>
        </div>
        <div class="bai-insights-kpi" role="listitem">
          <span class="bai-insights-kpi__icon bai-insights-kpi__icon--amber"><i class="pi pi-exclamation-triangle"></i></span>
          <div class="bai-insights-kpi__body">
            <span class="bai-insights-kpi__value">{{ s.highPriorityCount }}</span>
            <span class="bai-insights-kpi__label">High-Priority Signals</span>
          </div>
        </div>
        @if (s.agentSuggestionCount > 0) {
          <div class="bai-insights-kpi" role="listitem">
            <span class="bai-insights-kpi__icon bai-insights-kpi__icon--violet"><i class="pi pi-microchip-ai"></i></span>
            <div class="bai-insights-kpi__body">
              <span class="bai-insights-kpi__value">{{ s.agentSuggestionCount }}</span>
              <span class="bai-insights-kpi__label">Agent Suggestions</span>
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- KPI skeleton -->
      <div class="bai-insights-kpi-strip">
        @for (_ of [1,2,3]; track $index) {
          <div class="bai-insights-kpi">
            <p-skeleton width="2rem" height="2rem" borderRadius="0.5rem" />
            <div class="bai-insights-kpi__body">
              <p-skeleton width="3rem" height="1.25rem" />
              <p-skeleton width="5rem" height="0.875rem" styleClass="mt-1" />
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- ======================================================================
       Controls: search + filter
  ====================================================================== -->
  <div class="bai-insights-controls">
    <div class="bai-insights-search">
      <i class="pi pi-search bai-insights-search__icon"></i>
      <input
        type="search"
        class="bai-insights-search__input"
        placeholder="Search insights…"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event)"
        aria-label="Search insights"
      />
    </div>
    <div class="bai-insights-filter" role="group" aria-label="Filter by type">
      <button
        type="button"
        class="bai-insights-filter__btn"
        [class.bai-insights-filter__btn--active]="filterType() === 'all'"
        (click)="filterType.set('all')"
      >All</button>
      <button
        type="button"
        class="bai-insights-filter__btn"
        [class.bai-insights-filter__btn--active]="filterType() === 'ai'"
        (click)="filterType.set('ai')"
      ><i class="pi pi-microchip-ai"></i> AI</button>
      <button
        type="button"
        class="bai-insights-filter__btn"
        [class.bai-insights-filter__btn--active]="filterType() === 'manual'"
        (click)="filterType.set('manual')"
      ><i class="pi pi-pencil"></i> Notes</button>
    </div>
  </div>

  <!-- ======================================================================
       Feed
  ====================================================================== -->
  <div class="bai-insights-feed" role="feed" aria-label="Insights feed">

    @if (!state()) {
      <!-- Loading state -->
      @for (_ of [1,2,3,4]; track $index) {
        <div class="bai-insight-card bai-insight-card--skeleton">
          <div class="bai-insight-card__header">
            <p-skeleton width="60%" height="1.125rem" />
            <p-skeleton width="4rem" height="1.25rem" borderRadius="1rem" />
          </div>
          <p-skeleton width="100%" height="0.875rem" styleClass="mt-2" />
          <p-skeleton width="80%" height="0.875rem" styleClass="mt-1" />
          <div class="bai-insight-card__footer mt-3">
            <p-skeleton width="3rem" height="0.75rem" />
            <p-skeleton width="5rem" height="0.75rem" />
          </div>
        </div>
      }
    } @else if (filteredInsights().length === 0) {
      <!-- Empty state -->
      <div class="bai-insights-empty" role="status">
        <i class="pi pi-inbox bai-insights-empty__icon"></i>
        <p class="bai-insights-empty__title">No insights found</p>
        <p class="bai-insights-empty__body">
          @if (searchQuery()) {
            No insights match your search. Try a different keyword.
          } @else {
            AI insights will appear here as conversations and data are analyzed.
          }
        </p>
        @if (!searchQuery()) {
          <button type="button" class="bai-insights-btn bai-insights-btn--primary mt-4" (click)="openNoteModal()">
            <i class="pi pi-plus"></i> Add your first note
          </button>
        }
      </div>
    } @else {
      @for (insight of filteredInsights(); track insight.id) {
        @if (isTransientInsight(insight.id)) {
          <div class="bai-insight-card bai-insight-card--skeleton">
            <div class="bai-insight-card__header">
              <p-skeleton width="60%" height="1.125rem" />
              <p-skeleton width="4rem" height="1.25rem" borderRadius="1rem" />
            </div>
            <p-skeleton width="100%" height="0.875rem" styleClass="mt-2" />
            <p-skeleton width="80%" height="0.875rem" styleClass="mt-1" />
            <div class="bai-insight-card__footer mt-3">
              <p-skeleton width="3rem" height="0.75rem" />
              <p-skeleton width="5rem" height="0.75rem" />
            </div>
          </div>
        } @else {
          <article
            class="bai-insight-card"
            [class]="severityClass(insight.severity)"
            [attr.aria-label]="insight.title"
          >
            <!-- Card header -->
            <div class="bai-insight-card__header">
              <div class="bai-insight-card__title-group">
                <span class="bai-insight-card__source-icon" [attr.aria-label]="sourceLabel(insight.source)">
                  <i [class]="sourceIcon(insight.source)"></i>
                </span>
                <h3 class="bai-insight-card__title">{{ insight.title }}</h3>
              </div>
              <div class="bai-insight-card__badges">
                @if (insight.type === 'ai') {
                  <p-tag
                    value="AI Insight"
                    severity="info"
                    styleClass="bai-insight-tag bai-insight-tag--ai"
                  />
                } @else {
                  <p-tag
                    value="Manual Note"
                    severity="secondary"
                    styleClass="bai-insight-tag bai-insight-tag--manual"
                  />
                }
                <p-tag
                  [value]="sourceLabel(insight.source)"
                  severity="secondary"
                  styleClass="bai-insight-tag bai-insight-tag--source"
                />
                @if (insight.agentGenerated) {
                  <p-tag
                    value="Agent"
                    severity="warn"
                    styleClass="bai-insight-tag bai-insight-tag--agent"
                    pTooltip="Generated by AI Agent"
                    tooltipPosition="top"
                  />
                }
              </div>
            </div>

            <!-- Card body -->
            <p class="bai-insight-card__body">{{ insight.body }}</p>

            <!-- Tags -->
            @if (insight.tags.length > 0) {
              <div class="bai-insight-card__tags" aria-label="Tags">
                @for (tag of insight.tags; track tag) {
                  <span class="bai-insight-card__tag">{{ tag }}</span>
                }
              </div>
            }

            <!-- Card footer -->
            <div class="bai-insight-card__footer">
              <div class="bai-insight-card__meta">
                <span class="bai-insight-card__timestamp" [attr.title]="insight.createdAt">
                  <i class="pi pi-calendar"></i>
                  {{ insight.createdAt | date:'MMM d, y, h:mm a' }}
                </span>
                @if (insight.confidence !== null) {
                  <span
                    class="bai-insight-card__confidence"
                    [pTooltip]="'AI confidence: ' + confidencePct(insight.confidence)"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-chart-bar"></i>
                    {{ confidencePct(insight.confidence) }}
                  </span>
                }
              </div>
              <div class="bai-insight-card__actions">
                @if (insight.feedback) {
                  <span class="bai-insight-card__feedback-status">
                    @if (insight.feedback.sentiment === 'helpful') {
                      <i class="pi pi-thumbs-up-fill"></i> Helpful
                    } @else {
                      <i class="pi pi-thumbs-down-fill"></i> Not helpful
                    }
                  </span>
                } @else if (insight.type === 'ai') {
                  <button
                    type="button"
                    class="bai-insight-card__action-btn"
                    (click)="openFeedback(insight, 'helpful')"
                    aria-label="Mark as helpful"
                    pTooltip="Helpful"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-thumbs-up"></i>
                  </button>
                  <button
                    type="button"
                    class="bai-insight-card__action-btn bai-insight-card__action-btn--negative"
                    (click)="openFeedback(insight, 'notHelpful')"
                    aria-label="Mark as not helpful"
                    pTooltip="Not helpful"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-thumbs-down"></i>
                  </button>
                }
                @if (insight.sourceRef) {
                  <button
                    type="button"
                    class="bai-insight-card__action-btn bai-insight-card__action-btn--link"
                    [attr.aria-label]="'View source ' + insight.sourceRef"
                    pTooltip="View Source"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-external-link"></i>
                  </button>
                }
                <details class="bai-insight-card__kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  <summary class="bai-insight-card__kebab-btn" aria-label="Insight options">
                    <i class="pi pi-bars" aria-hidden="true"></i>
                  </summary>
                  <button
                    type="button"
                    class="bai-insight-card__kebab-action"
                    (click)="addInsightToCopilot(insight)"
                  >
                    Add to Copilot
                  </button>
                </details>
              </div>
            </div>
          </article>
        }
      }
    }
  </div>

  <!-- ======================================================================
       Feedback Modal
  ====================================================================== -->
  @if (feedbackModalOpen()) {
    <div
      class="bai-modal-backdrop"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="'Feedback for: ' + (feedbackInsight()?.title ?? '')"
      (click)="closeFeedbackModal()"
      (keydown.escape)="closeFeedbackModal()"
      tabindex="-1"
    >
      <div class="bai-modal" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
        <div class="bai-modal__header">
          <h3 class="bai-modal__title">
            @if (feedbackSentiment() === 'helpful') {
              <i class="pi pi-thumbs-up"></i> Helpful feedback
            } @else {
              <i class="pi pi-thumbs-down"></i> Not helpful — tell us why
            }
          </h3>
          <button
            type="button"
            class="bai-modal__close"
            (click)="closeFeedbackModal()"
            aria-label="Close feedback modal"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="bai-modal__body">
          <p class="bai-modal__insight-title">{{ feedbackInsight()?.title }}</p>

          <!-- Sentiment toggle -->
          <div class="bai-modal__field">
            <label class="bai-modal__label">Sentiment</label>
            <div class="bai-modal__toggle-group" role="group" aria-label="Sentiment">
              <button
                type="button"
                class="bai-modal__toggle"
                [class.bai-modal__toggle--active]="feedbackSentiment() === 'helpful'"
                (click)="feedbackSentiment.set('helpful'); feedbackReason.set(null)"
              >
                <i class="pi pi-thumbs-up"></i> Helpful
              </button>
              <button
                type="button"
                class="bai-modal__toggle"
                [class.bai-modal__toggle--active]="feedbackSentiment() === 'notHelpful'"
                (click)="feedbackSentiment.set('notHelpful'); feedbackReason.set(null)"
              >
                <i class="pi pi-thumbs-down"></i> Not helpful
              </button>
            </div>
          </div>

          <!-- Reason -->
          <div class="bai-modal__field">
            <label class="bai-modal__label">Reason <span class="bai-modal__optional">(optional)</span></label>
            <div class="bai-modal__reason-group" role="group" aria-label="Reason">
              @for (r of feedbackReasons(); track r.value) {
                <button
                  type="button"
                  class="bai-modal__reason-chip"
                  [class.bai-modal__reason-chip--active]="feedbackReason() === r.value"
                  (click)="feedbackReason.set(r.value)"
                >
                  {{ r.label }}
                </button>
              }
            </div>
          </div>

          <!-- Comment -->
          <div class="bai-modal__field">
            <label class="bai-modal__label" for="feedback-comment">
              Comment
              @if (feedbackCommentRequired()) {
                <span class="bai-modal__required">*</span>
              } @else {
                <span class="bai-modal__optional">(optional)</span>
              }
            </label>
            <textarea
              id="feedback-comment"
              class="bai-modal__textarea"
              rows="3"
              placeholder="Add a comment…"
              [ngModel]="feedbackComment()"
              (ngModelChange)="feedbackComment.set($event)"
            ></textarea>
          </div>
        </div>

        <div class="bai-modal__footer">
          <button type="button" class="bai-insights-btn bai-insights-btn--ghost" (click)="closeFeedbackModal()">
            Cancel
          </button>
          <button
            type="button"
            class="bai-insights-btn bai-insights-btn--primary"
            [disabled]="!feedbackCanSubmit()"
            (click)="submitFeedback()"
          >
            Save Feedback
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ======================================================================
       Add Note Modal
  ====================================================================== -->
  @if (noteModalOpen()) {
    <div
      class="bai-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Add advisor note"
      (click)="closeNoteModal()"
      (keydown.escape)="closeNoteModal()"
      tabindex="-1"
    >
      <div class="bai-modal" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
        <div class="bai-modal__header">
          <h3 class="bai-modal__title"><i class="pi pi-pencil"></i> Add Advisor Note</h3>
          <button
            type="button"
            class="bai-modal__close"
            (click)="closeNoteModal()"
            aria-label="Close note modal"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="bai-modal__body">
          <div class="bai-modal__field">
            <label class="bai-modal__label" for="note-title">
              Title <span class="bai-modal__optional">(optional)</span>
            </label>
            <input
              id="note-title"
              type="text"
              class="bai-modal__input"
              placeholder="Note title…"
              [ngModel]="noteTitle()"
              (ngModelChange)="noteTitle.set($event)"
            />
          </div>

          <div class="bai-modal__field">
            <label class="bai-modal__label" for="note-body">
              Note <span class="bai-modal__required">*</span>
            </label>
            <textarea
              id="note-body"
              class="bai-modal__textarea"
              rows="5"
              placeholder="Write your note here…"
              [ngModel]="noteBody()"
              (ngModelChange)="noteBody.set($event)"
            ></textarea>
          </div>

          <div class="bai-modal__field">
            <label class="bai-modal__label" for="note-timestamp">Timestamp</label>
            <input
              id="note-timestamp"
              type="datetime-local"
              class="bai-modal__input"
              [ngModel]="noteTimestamp()"
              (ngModelChange)="noteTimestamp.set($event)"
            />
          </div>
        </div>

        <div class="bai-modal__footer">
          <button type="button" class="bai-insights-btn bai-insights-btn--ghost" (click)="closeNoteModal()">
            Cancel
          </button>
          <button
            type="button"
            class="bai-insights-btn bai-insights-btn--primary"
            [disabled]="!noteCanSubmit()"
            (click)="submitNote()"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  }

</div>
